import { useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { type AnyFieldApi, useForm, useSelector } from '@tanstack/react-form';

import { useAuth } from '@nvision/identity-core';
import { DatePickerField, InputField, Text } from '@nvision/ui';

import {
  type CargoType,
  CargoTypeField,
  type Carrier,
  CarrierField,
  type CarrierService,
  CarrierServiceField,
  type CompanyUser,
  CompanyUserField,
  type ContractStatus,
  ContractStatusField,
  CountryField,
  type EquipmentType,
  EquipmentTypeField,
  type PortRow,
  type PortSearchFormValues,
  PortSearchModal,
  type PortType,
  PortTypeField,
  type PostalSearchFormValues,
  PostalSearchModal,
  type Postals,
  type PricingCategory,
  PricingCategoryField,
  type PricingType,
  PricingTypeField,
  type State,
  StateField,
  type SupplierDiversity,
  SupplierDiversityField,
  type TariffType,
  TariffTypeField,
  type TransportationMode,
  TransportationModeField,
} from '@/modules/entities';
import { getEffectiveDateBounds } from '@/utils/date';

import { normalizeContractFiltersUpdate } from '../../../data-provider';
import { getDefaultFormValues } from '../../../hooks';
import {
  type ContractsSearchFiltersFormValues,
  type ContractsSearchParams,
  contractsSearchFiltersFormSchema,
} from '../../../models';

import styles from './contracts-search-filters-form.module.css';

export type FormRef = {
  submit: () => void;
  reset: () => void;
  canSubmit: () => boolean;
};

interface ContractsSearchFiltersProps {
  formRef: React.RefObject<FormRef>;
  onSubmit: (filters: ContractsSearchParams) => void;
  initialValues: ContractsSearchFiltersFormValues;
  customerKey: number;
}

type PostalFieldName = 'destinationPostalCode' | 'originPostalCode';
type PortFieldName = 'destinationPortName' | 'originPortName';

export const ContractsSearchFiltersForm: React.FC<ContractsSearchFiltersProps> = ({
  formRef,
  onSubmit,
  initialValues,
  customerKey,
}) => {
  const [activePostalField, setActivePostalField] = useState<PostalFieldName | null>(null);
  const [postalDefaultValues, setPostalDefaultValues] = useState<PostalSearchFormValues>({} as PostalSearchFormValues);
  const [activePortField, setActivePortField] = useState<PortFieldName | null>(null);
  const [portDefaultValues, setPortDefaultValues] = useState<PortSearchFormValues>({} as PortSearchFormValues);

  const getSearchParams = (values: ContractsSearchFiltersFormValues): ContractsSearchParams =>
    normalizeContractFiltersUpdate(values);

  const auth = useAuth();
  const timeZone = auth.user?.profile.pref.timezone;

  const form = useForm({
    defaultValues: getDefaultFormValues(timeZone),
    onSubmit: async (props) => {
      if (props.value) {
        onSubmit(getSearchParams(props.value));
      }
    },
    validators: {
      onChange: contractsSearchFiltersFormSchema,
      onSubmit: contractsSearchFiltersFormSchema,
    },
  });

  useImperativeHandle(formRef, () => ({
    canSubmit: () => form.state.canSubmit,
    reset: () => form.reset(),
    submit: () => {
      onSubmit(getSearchParams(form.state.values));
    },
  }));

  const handleSubmit = (e: React.SubmitEvent) => {
    e.preventDefault();
    e.stopPropagation();
    form.handleSubmit();
  };

  const getError = (field: AnyFieldApi) =>
    field.state.meta.isTouched
      ? field.state.meta.errors
          .map((e: { message?: string }) => e?.message)
          .filter(Boolean)
          .join('. ')
      : undefined;

  useEffect(() => {
    Object.entries(initialValues).forEach(([key, value]) => {
      if ((key === 'effectiveFrom' || key === 'effectiveTo') && !value) {
        return;
      }

      form.setFieldValue(key as keyof ContractsSearchFiltersFormValues, value);
    });
  }, [initialValues, form]);

  const values = useSelector(form.store, (state) => state.values);

  const { effectiveFromMaxValue, effectiveToMinValue } = useMemo(
    () => getEffectiveDateBounds(values.effectiveFrom, values.effectiveTo, timeZone),
    [timeZone, values.effectiveFrom, values.effectiveTo],
  );

  const originCountryCode = values.originCountry?.id.toString();
  const destinationCountryCode = values.destinationCountry?.id.toString();

  const getPostalDefaultValues = (field: PostalFieldName): PostalSearchFormValues => {
    const isOrigin = field.startsWith('origin');
    const state = isOrigin ? values.originState : values.destinationState;
    const country = isOrigin ? values.originCountry : values.destinationCountry;

    return {
      additional: {
        city: (isOrigin ? values.originCity : values.destinationCity) ?? undefined,
        portPostal: values[field] ?? undefined,
        state: state ? { id: String(state.id), label: state.label } : undefined,
      },
      country: {
        id: country?.id.toString() ?? '',
        label: country?.label ?? '',
      },
    };
  };

  const openPostalSearchModal = (field: PostalFieldName) => {
    setPostalDefaultValues(getPostalDefaultValues(field));
    setActivePostalField(field);
  };

  const handlePostalSelectionChange = (selection: Postals) => {
    if (!activePostalField) {
      return;
    }

    form.setFieldValue(activePostalField, selection[0]?.postalCode ?? undefined);
    setActivePostalField(null);
  };

  const getPortDefaultValues = (field: PortFieldName): PortSearchFormValues => {
    const side = field.startsWith('origin') ? 'origin' : 'destination';
    const portType = values[`${side}PortType`];

    return {
      country: values[`${side}Country`] ?? undefined,
      portCity: values[`${side}City`] ?? undefined,
      portName: values[field] ?? undefined,
      portPostal: values[`${side}PostalCode`] ?? undefined,
      portState: values[`${side}State`] ?? null,
      portType: portType ? { id: String(portType.id), label: portType.label } : null,
    };
  };

  const openPortSearchModal = (field: PortFieldName) => {
    setPortDefaultValues(getPortDefaultValues(field));
    setActivePortField(field);
  };

  const handlePortSelectionChange = (port: PortRow) => {
    if (!activePortField) {
      return;
    }

    form.setFieldValue(activePortField, port.name ?? String(port.portKey));
    setActivePortField(null);
  };

  return (
    <form onSubmit={handleSubmit}>
      <form.Field name="carrierKey">
        {(field) => (
          <CarrierField
            value={field.state.value as unknown as Carrier | null}
            clearable
            defaultFilters={{
              customerKey,
            }}
            onSelectionChange={(carrier) => {
              field.handleChange(carrier ? { id: Number(carrier.id), label: carrier.label } : null);
            }}
            label="Transportation Provider"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>
      <form.Field name="uniqueName">
        {(field) => (
          <InputField
            value={field.state.value ?? ''}
            clearable
            onChange={(e) => field.handleChange(e.target.value || undefined)}
            label="Contract Number"
            placeholder="Enter"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="cargoType">
        {(field) => (
          <CargoTypeField
            value={field.state.value as unknown as CargoType | null}
            clearable
            onSelectionChange={(cargoType) => {
              field.handleChange(cargoType ? { id: String(cargoType.id), label: cargoType.label } : null);
            }}
            label="Cargo Type"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="effectiveFrom">
        {(field) => (
          <DatePickerField
            value={field.state.value ?? null}
            clearable
            maxValue={effectiveFromMaxValue}
            onChange={(date) => {
              field.handleChange(date ?? undefined);
            }}
            label="Effective From"
            size="sm"
            timeZone={timeZone}
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="effectiveTo">
        {(field) => (
          <DatePickerField
            value={field.state.value ?? null}
            clearable
            minValue={effectiveToMinValue}
            includeEndOfDay
            onChange={(date) => field.handleChange(date ?? undefined)}
            label="Effective To"
            size="sm"
            timeZone={timeZone}
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="contractName">
        {(field) => (
          <InputField
            value={field.state.value ?? ''}
            clearable
            onChange={(e) => field.handleChange(e.target.value || undefined)}
            label="Contract Name"
            placeholder="Enter"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="status">
        {(field) => (
          <ContractStatusField
            value={field.state.value as unknown as ContractStatus | null}
            clearable
            onSelectionChange={(status) =>
              field.handleChange(status ? { id: String(status.id), label: status.label } : null)
            }
            label="Contract Status"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="pricingBasis">
        {(field) => (
          <TariffTypeField
            value={field.state.value as unknown as TariffType | null}
            clearable
            onSelectionChange={(pricingBasis) =>
              field.handleChange(pricingBasis ? { id: String(pricingBasis.id), label: pricingBasis.label } : null)
            }
            label="Pricing Basis"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="pricingType">
        {(field) => (
          <PricingTypeField
            value={field.state.value as unknown as PricingType | null}
            clearable
            onSelectionChange={(pricingType) =>
              field.handleChange(pricingType ? { id: String(pricingType.id), label: pricingType.label } : null)
            }
            label="Pricing Type"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="pricingCategory">
        {(field) => (
          <PricingCategoryField
            value={field.state.value as unknown as PricingCategory | null}
            clearable
            onSelectionChange={(pricingCategory) =>
              field.handleChange(
                pricingCategory ? { id: String(pricingCategory.id), label: pricingCategory.label } : null,
              )
            }
            label="Pricing Category"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="supplierDiversity">
        {(field) => (
          <SupplierDiversityField
            value={field.state.value as unknown as SupplierDiversity | null}
            clearable
            onSelectionChange={(supplierDiversity) =>
              field.handleChange(
                supplierDiversity ? { id: String(supplierDiversity.id), label: supplierDiversity.label } : null,
              )
            }
            label="Supplier Diversity"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="createdBy">
        {(field) => (
          <CompanyUserField
            value={field.state.value as unknown as CompanyUser | null}
            clearable
            defaultFilters={{
              companyId: String(customerKey),
            }}
            onSelectionChange={(user) => {
              field.handleChange(user ? { id: Number(user.id), label: user.label } : null);
            }}
            label="Created By"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="modifiedBy">
        {(field) => (
          <CompanyUserField
            value={field.state.value as unknown as CompanyUser | null}
            clearable
            defaultFilters={{
              companyId: String(customerKey),
            }}
            onSelectionChange={(user) => {
              field.handleChange(user ? { id: Number(user.id), label: user.label } : null);
            }}
            label="Modified By"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <div className={styles.sectionHeader}>
        <Text size="sm" weight="medium" className={styles.sectionHeaderLabel}>
          Hide Origin
        </Text>
        <span className={styles.sectionHeaderLine} aria-hidden="true" />
      </div>

      <form.Field name="originCountry">
        {(field) => (
          <CountryField
            value={field.state.value ?? null}
            clearable
            onSelectionChange={(country) => {
              field.handleChange(country ?? null);
              form.setFieldValue('originState', null);
            }}
            label="Search by Origin Country"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="originState">
        {(field) => (
          <StateField
            key={originCountryCode ?? 'no-country'}
            countryCode={originCountryCode}
            value={field.state.value as unknown as State | null}
            clearable
            onSelectionChange={(state) =>
              field.handleChange(state ? { id: String(state.id), label: state.label } : null)
            }
            label="Search by Origin State"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="originPostalCode">
        {(field) => (
          <InputField
            value={field.state.value ?? ''}
            clearable
            readOnly
            onChange={() => field.handleChange(undefined)}
            onClick={() => openPostalSearchModal('originPostalCode')}
            label="Search by Origin Postal Code"
            placeholder="Enter"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="originCity">
        {(field) => (
          <InputField
            value={field.state.value ?? ''}
            clearable
            onChange={(e) => field.handleChange(e.target.value || undefined)}
            label="Search by Origin City"
            placeholder="Enter"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="originPortType">
        {(field) => (
          <PortTypeField
            value={field.state.value as unknown as PortType | null}
            clearable
            onSelectionChange={(portType) =>
              field.handleChange(portType ? { id: portType.id, label: portType.label } : null)
            }
            label="Search by Origin Port Type"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="originPortName">
        {(field) => (
          <InputField
            value={field.state.value ?? ''}
            clearable
            readOnly
            onChange={() => field.handleChange(undefined)}
            onClick={() => openPortSearchModal('originPortName')}
            label="Search by Origin Port Name"
            placeholder="Enter"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <div className={styles.sectionHeader}>
        <Text size="sm" weight="medium" className={styles.sectionHeaderLabel}>
          Hide Destination
        </Text>
        <span className={styles.sectionHeaderLine} aria-hidden="true" />
      </div>

      <form.Field name="destinationCountry">
        {(field) => (
          <CountryField
            value={field.state.value ?? null}
            clearable
            onSelectionChange={(country) => {
              field.handleChange(country ?? null);
              form.setFieldValue('destinationState', null);
            }}
            label="Search by Destination Country"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="destinationState">
        {(field) => (
          <StateField
            key={destinationCountryCode ?? 'no-destination-country'}
            countryCode={destinationCountryCode}
            value={field.state.value as unknown as State | null}
            clearable
            onSelectionChange={(state) =>
              field.handleChange(state ? { id: String(state.id), label: state.label } : null)
            }
            label="Search by Destination State"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="destinationPostalCode">
        {(field) => (
          <InputField
            value={field.state.value ?? ''}
            clearable
            readOnly
            onChange={() => field.handleChange(undefined)}
            onClick={() => openPostalSearchModal('destinationPostalCode')}
            label="Search by Destination Postal Code"
            placeholder="Enter"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="destinationCity">
        {(field) => (
          <InputField
            value={field.state.value ?? ''}
            clearable
            onChange={(e) => field.handleChange(e.target.value || undefined)}
            label="Search by Destination City"
            placeholder="Enter"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="destinationPortType">
        {(field) => (
          <PortTypeField
            value={field.state.value as unknown as PortType | null}
            clearable
            onSelectionChange={(portType) =>
              field.handleChange(portType ? { id: Number(portType.id), label: portType.label } : null)
            }
            label="Search by Destination Port Type"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="destinationPortName">
        {(field) => (
          <InputField
            value={field.state.value ?? ''}
            clearable
            readOnly
            onChange={() => field.handleChange(undefined)}
            onClick={() => openPortSearchModal('destinationPortName')}
            label="Search by Destination Port Name"
            placeholder="Enter"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="customerLaneId">
        {(field) => (
          <InputField
            value={field.state.value ?? ''}
            clearable
            onChange={(e) => field.handleChange(e.target.value || undefined)}
            label="Customer Lane ID"
            placeholder="Enter"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="transportationMode">
        {(field) => (
          <TransportationModeField
            value={field.state.value as unknown as TransportationMode | null}
            clearable
            onSelectionChange={(transportationMode) =>
              field.handleChange(
                transportationMode ? { id: String(transportationMode.id), label: transportationMode.label } : null,
              )
            }
            label="Transportation Mode"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="trailerKey">
        {(field) => (
          <EquipmentTypeField
            value={field.state.value as unknown as EquipmentType | null}
            clearable
            onSelectionChange={(equipmentType) =>
              field.handleChange(equipmentType ? { id: Number(equipmentType.id), label: equipmentType.label } : null)
            }
            label="Equipment Type"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="serviceKey">
        {(field) => (
          <CarrierServiceField
            value={field.state.value as unknown as CarrierService | null}
            clearable
            onSelectionChange={(carrierService) =>
              field.handleChange(carrierService ? { id: Number(carrierService.id), label: carrierService.label } : null)
            }
            label="Service Layer"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="approvedOn">
        {(field) => (
          <DatePickerField
            value={field.state.value}
            onChange={(date) => field.handleChange(date ?? undefined)}
            label="Approved On"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Subscribe
        selector={(state) => {
          return [state.canSubmit, state.isSubmitting, state.isPristine];
        }}
      >
        {([_canSubmit]) => (
          <button type="submit" style={{ display: 'none' }}>
            Hidden Submit
          </button>
        )}
      </form.Subscribe>

      <PostalSearchModal
        defaultValues={postalDefaultValues}
        isOpen={activePostalField !== null}
        setIsOpen={(open) => !open && setActivePostalField(null)}
        onSelectionChange={handlePostalSelectionChange}
      />
      <PortSearchModal
        key={activePortField ?? 'port-search'}
        defaultValues={portDefaultValues}
        isOpen={activePortField !== null}
        setIsOpen={(open) => !open && setActivePortField(null)}
        onSelectionChange={handlePortSelectionChange}
      />
    </form>
  );
};
